import React, { useState, useEffect } from 'react';
import { Trophy, TrendingUp, Zap, Award, Star, ChevronRight, Users, GraduationCap } from 'lucide-react';
import { schoolVotes, computedRankings } from '../lib/supabase';
import { schoolRankings2024, educationStats } from '../data/education-data';

// Simple horizontal bar component
const HorizontalBar = ({ value, maxValue, color = 'bg-blue-500' }) => {
  const percentage = Math.min((value / maxValue) * 100, 100);
  return (
    <div className="w-full bg-gray-200 rounded-full h-2.5">
      <div
        className={`h-2.5 rounded-full ${color}`}
        style={{ width: `${percentage}%` }}
      />
    </div>
  );
};

// Main Schools Snapshot Component for Homepage
export const SchoolsSnapshot = ({ setPage }) => {
  const [hotSchools, setHotSchools] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get top 5 academic schools from static data
  const topAcademicSchools = schoolRankings2024.slice(0, 5);
  const maxScore = topAcademicSchools[0]?.score || 100;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const { data, error } = await schoolVotes.getTopVoted(5);
        if (!error && data && data.length > 0) {
          setHotSchools(data);
        } else {
          setHotSchools([]);
        }
      } catch (err) {
        console.error('Error loading hot schools:', err);
        setHotSchools([]);
      }
      setLoading(false);
    };
    loadData();
  }, []);

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-black text-gray-800">School Rankings Snapshot</h2>
          <p className="text-gray-600">See how schools compare across Jamaica</p>
        </div>
        <button
          onClick={() => setPage('rankings')}
          className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
        >
          View All <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Top Academic Schools Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white">
            <div className="flex items-center gap-3">
              <GraduationCap className="w-8 h-8" />
              <div>
                <h3 className="font-bold text-lg">Academic Leaders</h3>
                <p className="text-blue-100 text-sm">Top CSEC performers</p>
              </div>
            </div>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              {topAcademicSchools.map((school, idx) => (
                <div key={school.school_name} className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                    idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-gray-400' : idx === 2 ? 'bg-amber-700' : 'bg-blue-400'
                  }`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-medium text-gray-800 truncate text-sm">{school.school_name}</span>
                      <span className="text-blue-600 font-bold text-sm ml-2">{school.score}</span>
                    </div>
                    <HorizontalBar value={school.score} maxValue={maxScore} color="bg-blue-500" />
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => setPage('rankings-academic')}
              className="w-full mt-4 py-2 bg-blue-50 text-blue-600 rounded-lg font-medium hover:bg-blue-100 transition-colors text-sm"
            >
              View Full Academic Rankings
            </button>
          </div>
        </div>

        {/* Hot Schools / Community Votes Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white">
            <div className="flex items-center gap-3">
              <Zap className="w-8 h-8" />
              <div>
                <h3 className="font-bold text-lg">Hot Schools</h3>
                <p className="text-purple-100 text-sm">Community favorites</p>
              </div>
            </div>
          </div>
          <div className="p-4">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : hotSchools.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">No votes yet — be the first to vote for your school.</p>
            ) : (
              <div className="space-y-3">
                {hotSchools.map((school, idx) => (
                  <div key={school.school_name} className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                      idx === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white' :
                      idx === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' :
                      idx === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700 text-white' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <span className="font-medium text-gray-800 text-sm">{school.school_name}</span>
                    </div>
                    <div className="flex items-center gap-1 text-purple-600">
                      <Users className="w-4 h-4" />
                      <span className="font-bold text-sm">{school.votes}</span>
                    </div>
                    {school.trend === 'up' && (
                      <TrendingUp className="w-4 h-4 text-brand-500" />
                    )}
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={() => setPage('rankings-popularity')}
              className="w-full mt-4 py-2 bg-purple-50 text-purple-600 rounded-lg font-medium hover:bg-purple-100 transition-colors text-sm"
            >
              Vote for Your School
            </button>
          </div>
        </div>

        {/* Extracurricular Champions Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden md:col-span-2 lg:col-span-1">
          <div className="bg-gradient-to-r from-brand-600 to-brand-800 p-4 text-white">
            <div className="flex items-center gap-3">
              <Trophy className="w-8 h-8" />
              <div>
                <h3 className="font-bold text-lg">Extracurricular Stars</h3>
                <p className="text-amber-100 text-sm">Sports, arts & more</p>
              </div>
            </div>
          </div>
          <div className="p-4">
            <p className="text-sm text-gray-600 mb-4">
              Achievements in sports, quiz, arts, robotics and more — built entirely from verified public submissions.
              Help put your school on the board.
            </p>
            <button
              onClick={() => setPage('submit-achievement')}
              className="w-full py-2 mb-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 transition-colors text-sm"
            >
              Submit an achievement
            </button>
            <button
              onClick={() => setPage('rankings-extracurricular')}
              className="w-full py-2 bg-amber-50 text-amber-600 rounded-lg font-medium hover:bg-amber-100 transition-colors text-sm"
            >
              View extracurricular rankings
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <div className="text-3xl font-black">{schoolRankings2024.length}</div>
          <div className="text-blue-100 text-sm">Schools Ranked (2024)</div>
        </div>
        <div className="bg-gradient-to-br from-brand-500 to-brand-600 rounded-xl p-4 text-white">
          <div className="text-3xl font-black">14</div>
          <div className="text-brand-100 text-sm">Parishes Covered</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <div className="text-3xl font-black">{educationStats.totalSchools}</div>
          <div className="text-purple-100 text-sm">Schools Listed</div>
        </div>
        <div className="bg-gradient-to-br from-brand-600 to-brand-800 rounded-xl p-4 text-white">
          <div className="text-3xl font-black">Free</div>
          <div className="text-amber-100 text-sm">Always Free to Use</div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="mt-6 bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-6 text-white">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="text-xl font-bold mb-1">Help us improve school data!</h3>
            <p className="text-slate-300 text-sm">Share your experience or report achievements we're missing</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setPage('share-sentiment')}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <Star className="w-4 h-4" /> Rate a School
            </button>
            <button
              onClick={() => setPage('submit-achievement')}
              className="px-4 py-2 bg-amber-500 hover:bg-amber-600 rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              <Award className="w-4 h-4" /> Add Achievement
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Compact version for sidebar or smaller spaces
export const SchoolsSnapshotCompact = ({ setPage }) => {
  const topSchools = schoolRankings2024.slice(0, 3);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-gray-800 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-amber-500" />
          Top Schools
        </h3>
        <button
          onClick={() => setPage('rankings')}
          className="text-blue-600 text-sm hover:underline"
        >
          See all
        </button>
      </div>
      <div className="space-y-3">
        {topSchools.map((school, idx) => (
          <div key={school.school_name} className="flex items-center gap-3">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white ${
              idx === 0 ? 'bg-amber-500' : idx === 1 ? 'bg-gray-400' : 'bg-amber-700'
            }`}>
              {idx + 1}
            </div>
            <span className="text-sm font-medium text-gray-700 flex-1">{school.school_name}</span>
            <span className="text-sm font-bold text-blue-600">{school.score}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SchoolsSnapshot;
